import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Globe2, Users, Send, Trash2, Loader2, Flag } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { UserAvatar, RankBadge } from '../components/RankBadge';
import VipBadge from '../components/VipBadge';
import ReportUserDialog from '../components/ReportUserDialog';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Feed() {
  const { user, bumpStats } = useAuth();
  const [online, setOnline] = useState([]);
  const [planets, setPlanets] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postingBody, setPostingBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(null);   // post object
  const [reportingPost, setReportingPost] = useState(null); // post being reported

  const loadPosts = () => {
    setLoading(true);
    api.get('/feed').then(r => setPosts(r.data || []))
       .catch(() => toast.error('Could not load feed'))
       .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPosts();
    api.get('/users/online').then(r => setOnline(r.data)).catch(() => {});
    api.get('/planets').then(r => setPlanets(r.data)).catch(() => {});
  }, []);

  const submitPost = async () => {
    const text = postingBody.trim();
    if (!text) return;
    if (text.length > 500) { toast.error('Post is too long (max 500 chars)'); return; }
    setPosting(true);
    try {
      const { data } = await api.post('/feed', { text });
      setPosts(p => [data, ...p]);
      setPostingBody('');
      bumpStats?.({ xp: 10 });
      toast.success('Posted ✨ +10 XP');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post');
    } finally {
      setPosting(false);
    }
  };

  const toggleLike = async (post) => {
    // Optimistic
    setPosts(list => list.map(p => p.id === post.id
      ? { ...p, liked_by_me: !p.liked_by_me, likes_count: p.likes_count + (p.liked_by_me ? -1 : 1) }
      : p));
    try {
      await api.post(`/feed/${post.id}/like`);
    } catch (e) {
      // Revert on failure
      setPosts(list => list.map(p => p.id === post.id
        ? { ...p, liked_by_me: !p.liked_by_me, likes_count: p.likes_count + (p.liked_by_me ? -1 : 1) }
        : p));
      toast.error(e?.response?.data?.detail || 'Could not like');
    }
  };

  const sharePost = async (post) => {
    const url = `${window.location.origin}/app/feed?post=${post.id}`;
    const shareData = {
      title: `${post.user_snapshot?.displayName || 'A traveler'} on WorldVibe`,
      text: post.text.slice(0, 120),
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      }
    } catch {
      /* user cancelled share */
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete(`/feed/${post.id}`);
      setPosts(list => list.filter(p => p.id !== post.id));
      toast.success('Post deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-4">
        <h2 className="font-display font-bold text-2xl">Feed</h2>

        {/* Composer */}
        <Card className="bg-[#161622] border-white/10">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <UserAvatar user={user} size={40} />
              <div className="flex-1">
                <Textarea
                  value={postingBody}
                  onChange={(e) => setPostingBody(e.target.value)}
                  placeholder={`What's on your mind, ${user?.displayName?.split(' ')[0] || 'traveler'}?`}
                  rows={2}
                  maxLength={500}
                  data-testid="feed-composer-text"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none"
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[11px] text-white/40">{postingBody.length}/500 · +10 XP per post</span>
                  <Button
                    onClick={submitPost}
                    disabled={posting || !postingBody.trim()}
                    data-testid="feed-composer-submit"
                    size="sm"
                    className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 text-white border-0"
                  >
                    {posting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Posting…</> : <><Send size={13} className="mr-1.5" /> Post</>}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading && <div className="text-center py-10 text-white/40" data-testid="feed-loading">Loading feed…</div>}
        {!loading && posts.length === 0 && <div className="text-center py-10 text-white/40" data-testid="feed-empty">Be the first to post ✨</div>}
        {posts.map(p => {
          const author = p.user_snapshot || {};
          const isMine = author.user_id === user?.user_id || user?.is_admin;
          return (
            <Card key={p.id} className="bg-[#161622] border-white/10" data-testid={`feed-post-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  {author.user_id ? (
                    <Link to={`/app/users/${author.user_id}`} data-testid={`feed-author-avatar-${p.id}`} title={`View ${author.displayName || 'user'}'s profile`} className="shrink-0">
                      <UserAvatar user={author} size={42} />
                    </Link>
                  ) : (
                    <UserAvatar user={author} size={42} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {author.user_id ? (
                        <Link to={`/app/users/${author.user_id}`} className="font-semibold hover:underline" data-testid={`feed-author-name-${p.id}`}>
                          {author.displayName || author.username}
                        </Link>
                      ) : (
                        <span className="font-semibold">{author.displayName || author.username}</span>
                      )}
                      <RankBadge rank={author.rank} />
                      <VipBadge active={author.is_vip} size="sm" />
                      <span className="text-xs text-white/40">· {timeAgo(p.created_at)}</span>
                      {isMine && (
                        <button
                          onClick={() => deletePost(p)}
                          data-testid={`feed-delete-${p.id}`}
                          className="ml-auto text-white/30 hover:text-rose-400 p-1 rounded"
                          aria-label="Delete post"
                          title="Delete post"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      {!isMine && (
                        <button
                          onClick={() => setReportingPost(p)}
                          data-testid={`feed-report-${p.id}`}
                          className="ml-auto text-white/30 hover:text-rose-400 p-1 rounded transition"
                          aria-label="Report this post"
                          title="Report this post"
                        >
                          <Flag size={14} />
                        </button>
                      )}
                    </div>
                    {p.planet_id && p.planet_name && (
                      <Link to={`/app/planets/${p.planet_id}`} className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200 mt-1"><Globe2 size={12} /> {p.planet_name}</Link>
                    )}
                    <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap break-words">{p.text}</p>
                    <div className="mt-4 flex items-center gap-5 text-sm text-white/60">
                      <button
                        onClick={() => toggleLike(p)}
                        data-testid={`feed-like-${p.id}`}
                        className={`flex items-center gap-1.5 transition ${p.liked_by_me ? 'text-pink-400' : 'hover:text-pink-400'}`}
                      >
                        <Heart size={16} fill={p.liked_by_me ? 'currentColor' : 'none'} /> {p.likes_count || 0}
                      </button>
                      <button
                        onClick={() => setCommentsOpen(p)}
                        data-testid={`feed-comment-${p.id}`}
                        className="flex items-center gap-1.5 hover:text-cyan-400 transition"
                      >
                        <MessageCircle size={16} /> {p.comments_count || 0}
                      </button>
                      <button
                        onClick={() => sharePost(p)}
                        data-testid={`feed-share-${p.id}`}
                        className="flex items-center gap-1.5 hover:text-white transition"
                      >
                        <Share2 size={16} /> Share
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Sidebar */}
      <div className="space-y-4">
        <Card className="bg-[#161622] border-white/10">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-semibold flex items-center gap-2"><Globe2 size={16} className="text-cyan-400" /> Trending</div>
              <Link to="/app/planets" className="text-xs text-cyan-300">See all</Link>
            </div>
            <div className="space-y-3">
              {planets.slice(0, 5).map(p => (
                <Link to={`/app/planets/${p.id}`} key={p.id} className="flex items-center gap-3 hover:bg-white/5 p-2 rounded-lg -m-2">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center text-xl`}>{p.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-white/50">{p.online} online</div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#161622] border-white/10">
          <CardContent className="p-5">
            <div className="font-display font-semibold mb-3 flex items-center gap-2"><Users size={16} className="text-fuchsia-400" /> Online Travelers</div>
            <div className="space-y-3">
              {online.slice(0, 6).map(u => (
                <Link
                  key={u.user_id}
                  to={`/app/users/${u.user_id}`}
                  className="flex items-center gap-3 hover:bg-white/5 rounded-xl px-1 -mx-1 py-1 transition"
                  data-testid={`feed-online-${u.user_id}`}
                >
                  <div className="relative">
                    <UserAvatar user={u} size={32} ring={false} />
                    <span className="absolute bottom-0 right-0 w-2 h-2 bg-emerald-400 border-2 border-[#161622] rounded-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{u.displayName}</div>
                    <div className="text-[11px] text-white/50">{u.country}</div>
                  </div>
                  <RankBadge rank={u.rank} />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comments dialog */}
      <CommentsDialog
        post={commentsOpen}
        onClose={() => setCommentsOpen(null)}
        onCommentAdded={() => setPosts(list => list.map(p => p.id === commentsOpen?.id ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p))}
        currentUser={user}
      />

      {/* Report dialog — flags either the post's author OR the post itself */}
      <ReportUserDialog
        open={!!reportingPost}
        onOpenChange={(o) => { if (!o) setReportingPost(null); }}
        target={reportingPost ? {
          user_id: reportingPost.user_snapshot?.user_id || reportingPost.author?.user_id,
          displayName: reportingPost.user_snapshot?.displayName || reportingPost.author?.displayName,
        } : null}
        content={reportingPost ? {
          type: 'feed_post',
          id: reportingPost.id,
          preview: reportingPost.text,
        } : null}
      />
    </div>
  );
}

// ─── Comments dialog ─────────────────────────────────────────────────────
function CommentsDialog({ post, onClose, onCommentAdded, currentUser }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!post) return;
    setLoading(true);
    setComments([]);
    api.get(`/feed/${post.id}/comments`)
      .then(r => setComments(r.data || []))
      .catch(() => toast.error('Could not load comments'))
      .finally(() => setLoading(false));
  }, [post?.id]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post(`/feed/${post.id}/comments`, { text: body });
      setComments(c => [...c, data]);
      setText('');
      onCommentAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not comment');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#161622] border-white/10 text-white max-w-md max-h-[80vh] flex flex-col p-0 overflow-hidden" data-testid="feed-comments-dialog">
        <div className="px-5 py-3 border-b border-white/5">
          <DialogTitle className="text-base">Comments</DialogTitle>
          <DialogDescription className="text-xs text-white/50">Reply to {post?.user_snapshot?.displayName || 'this traveler'}</DialogDescription>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading && <div className="text-center text-white/40 text-sm py-4">Loading…</div>}
          {!loading && comments.length === 0 && <div className="text-center text-white/40 text-sm py-6">No comments yet. Be the first ✨</div>}
          {comments.map(c => {
            const a = c.user_snapshot || {};
            return (
              <div key={c.id} className="flex gap-2.5" data-testid={`feed-comment-item-${c.id}`}>
                <UserAvatar user={a} size={32} />
                <div className="flex-1 min-w-0 bg-white/5 rounded-2xl px-3 py-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold truncate">{a.displayName || a.username}</span>
                    <RankBadge rank={a.rank} />
                    <VipBadge active={a.is_vip} size="sm" />
                    <span className="text-[10px] text-white/40">· {timeAgo(c.created_at)}</span>
                  </div>
                  <div className="text-sm mt-0.5 whitespace-pre-wrap break-words">{c.text}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-white/5 p-3 flex gap-2">
          <UserAvatar user={currentUser} size={32} />
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !sending && send()}
            placeholder="Write a comment…"
            maxLength={300}
            data-testid="feed-comments-input"
            className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/40"
          />
          <Button
            onClick={send}
            disabled={sending || !text.trim()}
            data-testid="feed-comments-send"
            size="icon"
            className="bg-cyan-500 hover:bg-cyan-600 text-white border-0 shrink-0"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
